import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { usePhoneCheck, EXISTING_PATIENT_MSG } from "@/hooks/usePhoneCheck";
import ExistingPatientAlert from "@/components/ExistingPatientAlert";

const TIERS = ["", "Silver", "Gold", "Platinum"];
const SOURCE_LABELS = {
  walk_in: "Walk-in", instagram: "Instagram", tiktok: "TikTok", referral: "Referral", google: "Google",
  facebook: "Facebook", ads: "Ads", ads_ots: "Ads OTS", ads_cs_cabang: "Ads CS Cabang",
};
const BASE_SOURCES = ["walk_in", "instagram", "tiktok", "ads", "referral", "google", "facebook"];
const FO_SOURCES = ["walk_in", "instagram", "tiktok", "ads", "ads_ots", "ads_cs_cabang", "referral", "google", "facebook"];
const TAG_OPTIONS = ["vip", "lifetime", "exclusive_member"];

const empty = {
  name: "",
  nik: "",
  no_ktp: false,
  phone: "",
  email: "",
  birthday: "",
  gender: "F",
  address: "",
  branch_id: "",
  source: "walk_in",
  medical_record_no: "",
  deposit_initial: 0,
  onboard_bonus_balance: 0,
  member_tier: "",
  notes: "",
  tags: [],
  existing_records: [],
};

export default function PatientFormDialog({ open, onOpenChange, patient, branches, onSaved }) {
  const isEdit = Boolean(patient?.id);
  const { user } = useAuth();
  const sources = user?.role === "fo_cs" ? FO_SOURCES : BASE_SOURCES;
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      if (isEdit) {
        setForm({
          name: patient.name || "",
          nik: patient.nik || "",
          no_ktp: patient.no_ktp || false,
          phone: patient.phone || "",
          email: patient.email || "",
          birthday: patient.birthday || "",
          gender: patient.gender || "F",
          address: patient.address || "",
          branch_id: patient.branch_id || "",
          source: patient.source || "walk_in",
          medical_record_no: patient.medical_record_no || "",
          deposit_initial: patient.deposit_initial ?? 0,
          deposit_balance: patient.deposit_balance ?? 0,
          onboard_bonus_balance: 0,
          member_tier: patient.member_tier || "",
          notes: patient.notes || "",
          tags: patient.tags || [],
          existing_records: [],
        });
      } else {
        setForm({ ...empty, branch_id: branches?.[0]?.id || "" });
      }
    }
  }, [open, patient, branches, isEdit]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const { existing } = usePhoneCheck(form.phone, open && !isEdit);
  const [autofilled, setAutofilled] = useState(false);
  useEffect(() => {
    if (!existing) {
      if (autofilled) {
        setForm((f) => ({ ...empty, phone: f.phone, branch_id: f.branch_id }));
        setAutofilled(false);
      }
      return;
    }
    setAutofilled(true);
    setForm((f) => ({
      ...f,
      name: existing.name || f.name, nik: existing.nik || "", no_ktp: !!existing.no_ktp,
      email: existing.email || "", birthday: existing.birthday || "", gender: existing.gender || f.gender,
      address: existing.address || "", branch_id: existing.branch_id || f.branch_id,
      source: existing.source || f.source, medical_record_no: existing.medical_record_no || "",
      member_tier: existing.member_tier || "", notes: existing.notes || "", tags: existing.tags || [],
    }));
  }, [existing]); // eslint-disable-line react-hooks/exhaustive-deps

  const finishWithExisting = (p) => {
    toast.info(EXISTING_PATIENT_MSG);
    onOpenChange(false);
    onSaved?.(p);
  };

  const toggleTag = (tag) =>
    setForm((f) => ({
      ...f,
      tags: f.tags.includes(tag) ? f.tags.filter((t) => t !== tag) : [...f.tags, tag],
    }));

  const addRecord = () =>
    setForm((f) => ({
      ...f,
      existing_records: [...(f.existing_records || []), { visit_date: "", treatment_name: "", diagnosis: "", notes: "" }],
    }));
  const updateRecord = (idx, k, v) =>
    setForm((f) => ({
      ...f,
      existing_records: (f.existing_records || []).map((r, i) => (i === idx ? { ...r, [k]: v } : r)),
    }));
  const removeRecord = (idx) =>
    setForm((f) => ({
      ...f,
      existing_records: (f.existing_records || []).filter((_, i) => i !== idx),
    }));

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.name || !form.phone || !form.branch_id) {
      toast.error("Nama, no HP, dan cabang wajib diisi");
      return;
    }
    if (form.nik && !/^\d{16}$/.test((form.nik || "").trim())) {
      toast.error("Jika diisi, NIK harus 16 digit angka sesuai KTP");
      return;
    }
    if (!isEdit && existing) { finishWithExisting(existing); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        no_ktp: !!form.no_ktp,
        nik: form.no_ktp ? null : ((form.nik || "").trim() || null),
        address: form.address || null,
        medical_record_no: (form.medical_record_no || "").trim() || null,
        deposit_initial: Number(form.deposit_initial) || 0,
        email: form.email || null,
        birthday: form.birthday || null,
        member_tier: form.member_tier || null,
      };
      if (isEdit) {
        delete payload.onboard_bonus_balance;
        delete payload.existing_records;
        if (payload.deposit_balance !== undefined)
          payload.deposit_balance = Number(payload.deposit_balance) || 0;
        await api.patch(`/patients/${patient.id}`, payload);
        toast.success("Pasien diperbarui");
      } else {
        payload.onboard_bonus_balance = Number(form.onboard_bonus_balance) || 0;
        payload.existing_records = (form.existing_records || []).filter(
          (r) => r.treatment_name || r.diagnosis || r.notes
        );
        const { data } = await api.post("/patients", payload);
        if (data.already_exists) { finishWithExisting(data); return; }
        toast.success("Pasien berhasil ditambahkan");
        onOpenChange(false);
        onSaved?.(data);
        return;
      }
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Gagal menyimpan");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="patient-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">
            {isEdit ? "Edit Pasien" : "Input Pasien Eksisting"}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Perbarui data pasien. Perubahan tersimpan realtime."
              : "Input data pasien dari sistem lama — lengkap dengan No. RM, saldo awal, dan riwayat tindakan. Field bertanda * wajib diisi."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Label>Nama Lengkap *</Label>
            <Input
              data-testid="patient-name-input"
              value={form.name}
              onChange={(e) => setField("name", e.target.value)}
              placeholder="cth. Aisha Sari"
              required
            />
          </div>
          <div className="md:col-span-2">
            <Label>Nomor Rekam Medis (No. RM)</Label>
            <Input
              data-testid="patient-rm-input"
              value={form.medical_record_no}
              onChange={(e) => setField("medical_record_no", e.target.value)}
              placeholder="cth. RM-000123 (diisi manual sesuai buku/sistem lama)"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Kosongkan untuk <b>nomor otomatis</b> (pasien baru). Isi manual untuk pasien lama — tidak akan ditimpa. Tampil di Database Pasien & dokumen cetak/struk.
            </p>
          </div>
          <div className="md:col-span-2">
            <Label>{form.no_ktp ? "NIK (KTP) — opsional (belum punya KTP)" : "NIK (KTP) — opsional, isi jika ada (16 digit)"}</Label>
            <Input
              data-testid="patient-nik-input"
              value={form.nik}
              inputMode="numeric"
              maxLength={16}
              disabled={form.no_ktp}
              onChange={(e) => setField("nik", e.target.value.replace(/\D/g, "").slice(0, 16))}
              placeholder={form.no_ktp ? "Tidak diperlukan — pasien di bawah umur" : "cth. 3174xxxxxxxxxxxx (opsional)"}
            />
            <label className="mt-2 flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                data-testid="patient-no-ktp-checkbox"
                checked={!!form.no_ktp}
                onChange={(e) => setField("no_ktp", e.target.checked)}
              />
              <span className="text-sm">Pasien belum memiliki KTP (di bawah 17 tahun)</span>
            </label>
            <p className="mt-1 text-xs text-muted-foreground">
              {form.no_ktp
                ? "NIK dikosongkan — identitas FHIR diberi penanda sementara 'UNDER_17_PENDING'."
                : "Wajib untuk kepatuhan SATUSEHAT Kemenkes (identitas FHIR pasien)."}
            </p>
          </div>
          <div>
            <Label>No. WhatsApp *</Label>
            <Input
              data-testid="patient-phone-input"
              value={form.phone}
              onChange={(e) => setField("phone", e.target.value)}
              placeholder="08xx..."
              required
            />
          </div>
          {!isEdit && existing && (
            <div className="md:col-span-2"><ExistingPatientAlert patient={existing} /></div>
          )}
          <div>
            <Label>Email</Label>
            <Input
              data-testid="patient-email-input"
              type="email"
              value={form.email}
              onChange={(e) => setField("email", e.target.value)}
              placeholder="pasien@email.com"
            />
          </div>
          <div>
            <Label>Tanggal Lahir</Label>
            <Input
              type="date"
              data-testid="patient-birthday-input"
              value={form.birthday}
              onChange={(e) => setField("birthday", e.target.value)}
            />
          </div>
          <div>
            <Label>Gender</Label>
            <Select value={form.gender} onValueChange={(v) => setField("gender", v)}>
              <SelectTrigger data-testid="patient-gender-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="F">Wanita</SelectItem>
                <SelectItem value="M">Pria</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Cabang *</Label>
            <Select value={form.branch_id} onValueChange={(v) => setField("branch_id", v)}>
              <SelectTrigger data-testid="patient-branch-select">
                <SelectValue placeholder="Pilih cabang" />
              </SelectTrigger>
              <SelectContent>
                {branches?.map((b) => (
                  <SelectItem key={b.id} value={b.id}>
                    {b.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Sumber</Label>
            <Select value={form.source} onValueChange={(v) => setField("source", v)}>
              <SelectTrigger data-testid="patient-source-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {sources.map((s) => (
                  <SelectItem key={s} value={s} data-testid={`patient-source-opt-${s}`}>
                    {SOURCE_LABELS[s]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>{isEdit ? "Saldo Deposit Saat Ini" : "Deposit Awal"} (Rp)</Label>
            <Input
              type="number"
              min="0"
              step="1"
              data-testid="patient-deposit-input"
              disabled={!isEdit && !!existing}
              value={isEdit ? form.deposit_balance ?? 0 : form.deposit_initial}
              onChange={(e) =>
                setField(isEdit ? "deposit_balance" : "deposit_initial", e.target.value)
              }
            />
          </div>
          {!isEdit && (
            <div>
              <Label>Cashback Awal / Bonus (Rp)</Label>
              <Input
                type="number"
                min="0"
                step="1"
                data-testid="patient-cashback-input"
                disabled={!!existing}
                value={form.onboard_bonus_balance}
                onChange={(e) => setField("onboard_bonus_balance", e.target.value)}
                placeholder="Sisa saldo bonus cashback dari sistem lama"
              />
            </div>
          )}
          <div>
            <Label>Member Tier</Label>
            <Select
              value={form.member_tier || "none"}
              onValueChange={(v) => setField("member_tier", v === "none" ? "" : v)}
            >
              <SelectTrigger data-testid="patient-tier-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— Tanpa Tier —</SelectItem>
                {TIERS.filter(Boolean).map((t) => (
                  <SelectItem key={t} value={t}>
                    {t}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Label>Tags</Label>
            <div className="mt-1.5 flex flex-wrap gap-2">
              {TAG_OPTIONS.map((t) => (
                <button
                  key={t}
                  type="button"
                  data-testid={`tag-${t}`}
                  onClick={() => toggleTag(t)}
                  className={`rounded-full border px-3 py-1 text-xs capitalize transition-colors ${
                    form.tags.includes(t)
                      ? "border-foreground bg-foreground text-background"
                      : "border-border hover:bg-secondary"
                  }`}
                >
                  {t.replace("_", " ")}
                </button>
              ))}
            </div>
          </div>
          <div className="md:col-span-2">
            <Label>Alamat</Label>
            <Textarea
              data-testid="patient-address-input"
              value={form.address}
              onChange={(e) => setField("address", e.target.value)}
              placeholder="Alamat sesuai domisili / KTP"
              rows={2}
            />
          </div>
          <div className="md:col-span-2">
            <Label>Catatan</Label>
            <Textarea
              data-testid="patient-notes-input"
              value={form.notes}
              onChange={(e) => setField("notes", e.target.value)}
              placeholder="Riwayat alergi, preferensi treatment, dll."
              rows={3}
            />
          </div>

          {!isEdit && (
            <div className="md:col-span-2 rounded-md border border-border bg-secondary/40 p-3 space-y-3" data-testid="existing-records-section">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Riwayat Tindakan (Rekam Medis Sistem Lama)</Label>
                  <p className="text-xs text-muted-foreground">Tambahkan kunjungan/tindakan sebelumnya. Otomatis masuk ke Rekam Medis pasien.</p>
                </div>
                <Button type="button" size="sm" variant="outline" data-testid="add-record-btn" onClick={addRecord}>
                  + Tambah Riwayat
                </Button>
              </div>
              {(form.existing_records || []).length === 0 && (
                <p className="text-xs text-muted-foreground italic">Belum ada riwayat. Klik "Tambah Riwayat" untuk menambah.</p>
              )}
              {(form.existing_records || []).map((r, idx) => (
                <div key={idx} className="rounded-md border border-border bg-card p-2.5 space-y-2" data-testid={`record-row-${idx}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-muted-foreground">Riwayat #{idx + 1}</span>
                    <button type="button" data-testid={`remove-record-${idx}`} onClick={() => removeRecord(idx)} className="text-xs text-rose-600 hover:underline">Hapus</button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">Tanggal Kunjungan</Label>
                      <Input type="date" data-testid={`record-date-${idx}`} value={r.visit_date} onChange={(e) => updateRecord(idx, "visit_date", e.target.value)} />
                    </div>
                    <div>
                      <Label className="text-xs">Tindakan / Treatment</Label>
                      <Input data-testid={`record-treatment-${idx}`} value={r.treatment_name} onChange={(e) => updateRecord(idx, "treatment_name", e.target.value)} placeholder="cth. Facial Brightening" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Diagnosa</Label>
                    <Input data-testid={`record-diagnosis-${idx}`} value={r.diagnosis} onChange={(e) => updateRecord(idx, "diagnosis", e.target.value)} placeholder="cth. Acne Vulgaris" />
                  </div>
                  <div>
                    <Label className="text-xs">Catatan Dokter</Label>
                    <Textarea data-testid={`record-notes-${idx}`} value={r.notes} onChange={(e) => updateRecord(idx, "notes", e.target.value)} rows={2} placeholder="Catatan tindakan / anjuran" />
                  </div>
                </div>
              ))}
            </div>
          )}

          <DialogFooter className="md:col-span-2 mt-2">
            <Button
              type="button"
              variant="outline"
              data-testid="patient-cancel-btn"
              onClick={() => onOpenChange(false)}
            >
              Batal
            </Button>
            <Button type="submit" data-testid="patient-save-btn" disabled={saving}>
              {saving ? "Menyimpan..." : isEdit ? "Simpan Perubahan" : existing ? "Gunakan Pasien Ini" : "Simpan Pasien"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
